import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Youtube, Mail, MapPin, MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations, companyInfo } from '../data/mockData';
import { GOLD, NAVY, CREAM, alpha } from '../theme/palette';
import { usePageLayout } from '../context/SiteLayoutContext';

// Belmond Cathedral theme tokens
const SERIF = { fontFamily: '"Fraunces", serif' };

const Footer = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const brOffice = companyInfo.offices.brazil;
  const usOffice = companyInfo.offices.usa;
  const pb = usePageLayout('footer');
  const col = (key) => { const p = pb.props('columns', key); return { style: p.style, 'data-testid': `footer-col-${key}` }; };
  const link = (key) => pb.visible('links', key);

  return (
    <footer className="py-20 px-6 lg:px-10" style={{ background: NAVY, color: CREAM }} data-testid="site-footer" data-page-block="footer">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Brand + history */}
        <div className="lg:col-span-5" {...col('brand')}>
          <img src="/logo-demarre.png" alt="Demarré Tours" className="h-20 w-auto mb-5" style={{ filter: 'brightness(0) invert(1) drop-shadow(0 4px 20px color-mix(in srgb, var(--site-gold) 40%, transparent))' }} />
          <div className="text-[10px] tracking-[0.35em] uppercase mb-5" style={{ color: GOLD }}>{language === 'en' ? 'Catholic Pilgrimages · Since 1996' : language === 'es' ? 'Peregrinaciones Católicas · Desde 1996' : 'Peregrinações Católicas · Desde 1996'}</div>
          <p className="text-sm text-white/70 max-w-md mb-7 leading-relaxed">
            {companyInfo.history[language]}
          </p>
          <div className="flex gap-3 mb-7">
            <a href={companyInfo.social.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border flex items-center justify-center hover:bg-white/10 transition" style={{ borderColor: `${alpha(GOLD, '55')}` }} aria-label="Facebook"><Facebook className="w-4 h-4" /></a>
            <a href={companyInfo.social.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border flex items-center justify-center hover:bg-white/10 transition" style={{ borderColor: `${alpha(GOLD, '55')}` }} aria-label="Instagram"><Instagram className="w-4 h-4" /></a>
            <a href={companyInfo.social.youtube} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border flex items-center justify-center hover:bg-white/10 transition" style={{ borderColor: `${alpha(GOLD, '55')}` }} aria-label="YouTube"><Youtube className="w-4 h-4" /></a>
          </div>
          <form className="w-full max-w-md">
            <label className="text-[10px] tracking-[0.35em] uppercase mb-2 block" style={{ color: GOLD }}>Receba a brochura 2026</label>
            <div className="flex gap-2">
              <input type="email" placeholder="seu@email.com" className="flex-1 bg-transparent border-b py-3 text-sm placeholder-white/40 focus:outline-none" style={{ borderColor: `${alpha(GOLD, '55')}`, color: '#fff' }} />
              <button type="submit" className="text-[10px] tracking-[0.3em] uppercase font-semibold px-5 py-3" style={{ background: GOLD, color: NAVY }}>Enviar</button>
            </div>
          </form>
        </div>

        {/* Explore */}
        <div className="lg:col-span-2" {...col('explore')}>
          <div className="text-[10px] tracking-[0.35em] uppercase mb-5" style={{ color: GOLD }}>Explore</div>
          <ul className="space-y-2.5 text-sm text-white/75">
            {link('brazil') && <li><Link to="/pilgrimages?departure=brazil" className="hover:text-white transition">{t.nav.brazilDepartures}</Link></li>}
            {link('usa') && <li><Link to="/pilgrimages?departure=usa" className="hover:text-white transition">{t.nav.usaDepartures}</Link></li>}
            {link('gallery') && <li><Link to="/galeria" className="hover:text-white transition">{language === 'pt' ? 'Galeria' : language === 'en' ? 'Gallery' : 'Galería'}</Link></li>}
            {link('about') && <li><Link to="/about" className="hover:text-white transition">{t.nav.about}</Link></li>}
            {link('faq') && <li><Link to="/faq" className="hover:text-white transition">{t.nav.faq}</Link></li>}
            {link('compare') && <li><Link to="/comparar" className="hover:text-white transition">{language === 'pt' ? 'Comparar' : language === 'en' ? 'Compare' : 'Comparar'}</Link></li>}
            {link('ambassadors') && <li className="pt-2 mt-2 border-t" style={{ borderColor: `${alpha(GOLD, '22')}` }}>
              <Link
                to="/embaixador/login"
                className="inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase font-semibold hover:text-white transition"
                style={{ color: GOLD }}
                data-testid="footer-embaixadores-link"
              >
                🎖 {language === 'pt' ? 'Embaixadores' : language === 'en' ? 'Ambassadors' : 'Embajadores'}
              </Link>
            </li>}
          </ul>
        </div>

        {/* Offices */}
        <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-6" {...col('offices')}>
          <div>
            <div className="text-[10px] tracking-[0.35em] uppercase mb-5 flex items-center gap-2" style={{ color: GOLD }}>
              <MapPin className="w-3 h-3" /> {brOffice.name[language]}
            </div>
            <p className="text-sm text-white/75 leading-relaxed">{brOffice.address}<br/>{brOffice.city}</p>
            <a href={`https://wa.me/${brOffice.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-3 text-sm hover:text-white transition" style={{ color: GOLD }}>
              <MessageCircle className="w-4 h-4" /> {brOffice.phone}
            </a>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.35em] uppercase mb-5 flex items-center gap-2" style={{ color: GOLD }}>
              <MapPin className="w-3 h-3" /> {usOffice.name[language]}
            </div>
            <p className="text-sm text-white/75 leading-relaxed">{usOffice.address}<br/>{usOffice.city}</p>
            <a href={`https://wa.me/${usOffice.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-3 text-sm hover:text-white transition" style={{ color: GOLD }}>
              <MessageCircle className="w-4 h-4" /> {usOffice.phone}
            </a>
            <a href={`mailto:${companyInfo.email}`} className="inline-flex items-center gap-2 mt-2 text-sm hover:text-white transition" style={{ color: GOLD }}>
              <Mail className="w-4 h-4" /> {companyInfo.email}
            </a>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      {pb.visible('columns', 'bottom') && <div className="max-w-7xl mx-auto mt-14 pt-8 border-t flex flex-wrap justify-between gap-3 text-[10px] tracking-[0.35em] uppercase text-white/40" style={{ borderColor: `${alpha(GOLD, '22')}` }} data-testid="footer-col-bottom">
        <span>© {new Date().getFullYear()} {companyInfo.name} · {t.footer.rights}</span>
        <Link to="/privacy" className="hover:text-white transition">{t.footer.privacy}</Link>
      </div>}
    </footer>
  );
};

export default Footer;
